import { Alert, Button, Grid, Select, Space, Table, Tag, Typography } from "antd";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import dayjs from "dayjs";
import { api } from "../app/api.ts";
import { excuseKeys, excuseStatusColor, excuseStatuses, type ExcuseRequestStatus, type ExcuseRequestSummary } from "../app/excuses.ts";
import { formatSchoolDateTime } from "../app/time.ts";
import { ExcuseRequestDrawer } from "../components/ExcuseRequestDrawer.tsx";

const PAGE_SIZE = 25;
const DATE_FORMAT = "DD.MM.YYYY";

/** Spec story 43: parents' absence excuse requests for review (GET /attendance/excuse-requests). Requires attendance.excuse. */
export function ExcuseRequestsPage() {
  const { t } = useTranslation();
  const screens = Grid.useBreakpoint();
  const [page, setPage] = useState(1);
  const [status, setStatus] = useState<ExcuseRequestStatus | undefined>("Pending");
  const [openId, setOpenId] = useState<string | null>(null);

  const query = useQuery({
    queryKey: excuseKeys.list({ status, page }),
    queryFn: async () => {
      const { data, error } = await api.GET("/attendance/excuse-requests", {
        params: { query: { Page: page, PageSize: PAGE_SIZE, ...(status === undefined ? {} : { Status: status }) } },
      });
      if (error || !data) throw new Error(t("app.error"));
      return data;
    },
    placeholderData: keepPreviousData,
  });

  const period = (r: ExcuseRequestSummary) =>
    r.from === r.to ? dayjs(r.from).format(DATE_FORMAT) : t("attendance.excuses.period", { from: dayjs(r.from).format(DATE_FORMAT), to: dayjs(r.to).format(DATE_FORMAT) });
  const columns = [
    { key: "submittedAt", title: t("attendance.excuses.columns.submittedAt"), width: 150, render: (_: unknown, r: ExcuseRequestSummary) => formatSchoolDateTime(r.submittedAt) },
    {
      key: "student",
      title: t("attendance.excuses.columns.student"),
      width: 200,
      render: (_: unknown, r: ExcuseRequestSummary) => (
        <Space orientation="vertical" size={0}>
          <Link to={`/students/${r.studentId}`}>{r.studentName}</Link>
          <Typography.Text type="secondary">{r.submittedByName}</Typography.Text>
        </Space>
      ),
    },
    { key: "period", title: t("attendance.excuses.columns.period"), width: 200, render: (_: unknown, r: ExcuseRequestSummary) => period(r) },
    {
      key: "reason",
      title: t("attendance.excuses.columns.reason"),
      render: (_: unknown, r: ExcuseRequestSummary) => (
        <Typography.Paragraph style={{ margin: 0, whiteSpace: "pre-wrap" }} ellipsis={{ rows: 2 }}>
          {r.reason}
        </Typography.Paragraph>
      ),
    },
    {
      key: "status",
      title: t("attendance.excuses.columns.status"),
      width: 170,
      render: (_: unknown, r: ExcuseRequestSummary) => (
        <Space orientation="vertical" size={0}>
          <Tag color={excuseStatusColor[r.status]}>{t(`attendance.excuses.statuses.${r.status}`)}</Tag>
          {r.decidedByName && <Typography.Text type="secondary">{r.decidedByName}</Typography.Text>}
        </Space>
      ),
    },
    {
      key: "actions",
      title: "",
      width: 140,
      render: (_: unknown, r: ExcuseRequestSummary) => (
        <Button aria-label={t("attendance.excuses.reviewFor", { name: r.studentName })} onClick={() => setOpenId(r.id)}>
          {t("attendance.excuses.review")}
        </Button>
      ),
    },
  ];

  return (
    <Space orientation="vertical" size="middle" style={{ width: "100%" }}>
      <div>
        <Typography.Title level={3} style={{ margin: 0 }}>
          {t("attendance.excuses.title")}
        </Typography.Title>
        <Typography.Text type="secondary">{t("attendance.excuses.subtitle")}</Typography.Text>
      </div>
      <Select<ExcuseRequestStatus | undefined>
        allowClear
        value={status}
        placeholder={t("attendance.excuses.allStatuses")}
        aria-label={t("attendance.excuses.statusFilter")}
        style={{ width: screens.md ? 220 : "100%" }}
        options={excuseStatuses.map((s) => ({ value: s, label: t(`attendance.excuses.statuses.${s}`) }))}
        onChange={(value) => {
          setStatus(value);
          setPage(1);
        }}
      />
      {query.isError && <Alert type="error" showIcon title={t("app.error")} action={<Button onClick={() => void query.refetch()}>{t("app.retry")}</Button>} />}
      <Table<ExcuseRequestSummary>
        rowKey="id"
        size={screens.md ? "middle" : "small"}
        loading={query.isPending || (query.isFetching && query.isPlaceholderData)}
        columns={columns}
        dataSource={query.data?.items ?? []}
        locale={{ emptyText: t("attendance.excuses.empty") }}
        scroll={{ x: 1000 }}
        pagination={{
          current: page,
          pageSize: PAGE_SIZE,
          total: Number(query.data?.totalCount ?? 0),
          showSizeChanger: false,
          hideOnSinglePage: true,
          showTotal: (total: number) => t("attendance.excuses.total", { count: total }),
          onChange: setPage,
        }}
      />
      <ExcuseRequestDrawer id={openId} onClose={() => setOpenId(null)} />
    </Space>
  );
}
